import { createTheme } from "@mui/material/styles";

/* ═══════════════════════════════════════════════════════════════════════════════
   ReonBook — central theme
   ───────────────────────────────────────────────────────────────────────────────
   ONE place to manage the app's colour scheme and font style.

   1. Edit the TOKENS below (colors, fonts, radii, shadows).
   2. The MUI theme at the bottom is built from them, so every MUI component
      follows automatically.
   3. Pages that style with `sx` can import the same tokens instead of hard-coding
      hex values:

        import { colors, gradients, fonts, radii, shadows } from "../utils/theme";
        <Box sx={{ bgcolor: colors.primary.tint, borderRadius: `${radii.xl}px` }} />

      …or read them from the theme:  theme.custom.colors.primary.main
   4. Plain CSS can use the CSS variables published on :root (see MuiCssBaseline),
      e.g.  color: var(--rb-primary);
   ═══════════════════════════════════════════════════════════════════════════════ */

export const APPBAR_HEIGHT = 64;

// ─── Colours ──────────────────────────────────────────────────────────────────
export const colors = {
  primary: {
    main:     '#1877F2',   // brand blue
    dark:     '#0A5EC0',   // hover / pressed
    darker:   '#0855A8',   // gradient end
    light:    '#E7F0FF',   // tinted backgrounds
    tint:     '#EBF3FF',   // icon chips, selected rows
    surface:  '#F7FAFF',   // table headers, hover rows
    selected: '#D1E4FD',   // selected rows (clearly stronger than hover)
  },
  secondary: { main: '#42B72A', dark: '#2E8A1B', light: '#EBF9E6' },

  success: { main: '#10B981', dark: '#2E7D32', tint: '#E8F7EE' },
  warning: { main: '#F59E0B', dark: '#B45309', tint: '#FFF4E5' },
  error:   { main: '#EF4444', dark: '#D32F2F', darker: '#C62828', tint: '#FDECEA' },
  info:    { main: '#1877F2' },

  // Cash / "money in" green used for cash-related sections
  accent: { green: '#16A34A', greenDark: '#0D7A37', greenTint: '#E8F7EE' },

  neutral: {
    bg:          '#F3F6FB',   // page background
    surface:     '#FFFFFF',   // cards, dialogs
    field:       '#FAFBFC',   // input fill
    ink:         '#0F172A',   // headings
    text:        '#1C1E21',   // body text
    textSub:     '#65676B',   // secondary text
    sub:         '#64748B',   // captions
    muted:       '#94A3B8',   // placeholders, disabled
    border:      '#EEF1F6',   // hairline borders
    borderStrong:'#E4E6EB',   // input borders
    divider:     'rgba(0,0,0,0.10)',
    black:       '#000000',
    line:        '#E2E8F0',   // default borders / dividers
    lineStrong:  '#CBD5E1',   // stronger borders, disabled outlines
    surfaceAlt:  '#F8FAFC',   // zebra rows, subtle panels
    surfaceMuted:'#F1F5F9',   // muted panels, hovers
    gridHeader:  '#BEC3C9',   // data-grid header band (= grey 300)
  },

  grey: {
    50:  '#F0F2F5', 100: '#E4E6EA', 200: '#CCD0D5', 300: '#BEC3C9', 400: '#A8B0B9',
    500: '#8D949E', 600: '#777E8B', 700: '#606770', 800: '#444950', 900: '#1C1E21',
  },
};

// Translucent versions of the brand blue (hovers, focus rings, scrollbars)
export const primaryAlpha = (a) => `rgba(24,119,242,${a})`;

export const gradients = {
  primary:    `linear-gradient(135deg, ${colors.primary.main} 0%, ${colors.primary.darker} 100%)`,
  primaryRow: `linear-gradient(90deg, ${colors.primary.main} 0%, ${colors.primary.darker} 100%)`,
  green:      `linear-gradient(135deg, ${colors.accent.green} 0%, ${colors.accent.greenDark} 100%)`,
  danger:     'linear-gradient(90deg, #d32f2f 0%, #f44336 100%)',
  page:       'linear-gradient(180deg, #F3F6FC 0%, #EEF2FA 100%)',
  cardHeader: 'linear-gradient(160deg, #FFFFFF 0%, #F7FAFF 100%)',
};

// ─── Fonts ────────────────────────────────────────────────────────────────────
// To change the app's font: edit `family` (and add the @import / <link> for a web font
// if it is not a system font). Everything below follows.
export const fonts = {
  // IBM Plex Sans, loaded from Google Fonts in public/index.html; system fonts are the fallback.
  family: '"IBM Plex Sans", "Segoe UI", "Calibri", "Aptos", "Roboto", "Helvetica", "Arial", sans-serif',
  mono:   '"Cascadia Code", "Consolas", "Courier New", monospace',
  weights: { regular: 400, medium: 500, semibold: 600, bold: 700, heavy: 800 },
  // rem sizes used by compact modern pages
  sizes: { xs: '0.66rem', sm: '0.74rem', md: '0.82rem', base: '0.875rem', lg: '1rem', xl: '1.25rem' },
};

// ─── Shape & elevation ────────────────────────────────────────────────────────
export const radii = { xs: 6, sm: 8, md: 10, lg: 12, xl: 14, xxl: 16, pill: 999 };

export const shadows = {
  card:        '0 1px 2px rgba(15,23,42,0.04), 0 8px 24px rgba(15,23,42,0.06)',
  cardHover:   '0 8px 20px rgba(24,119,242,0.14)',
  popover:     '0 12px 32px rgba(15,23,42,0.14)',
  dialog:      '0 20px 50px rgba(15,23,42,0.18)',
  glowPrimary: '0 6px 16px rgba(24,119,242,0.30)',
  glowPrimaryHover: '0 10px 24px rgba(24,119,242,0.42)',
  focusRing:   `0 0 0 3px ${primaryAlpha(0.15)}`,
  bar:         '0 -3px 12px rgba(15,23,42,0.04)',
};

// ─── Details-page design preset ───────────────────────────────────────────────
// The look shared by the list / "details" pages (ClientDetail2, ProductDetail2, TDSDetails, the
// invoice / order / voucher pages …): shell, header strip + title bar, data grid, mobile cards,
// bulk-action bar and the delete-confirmation dialog. Import the pieces you need, e.g.
//
//   import { detailPage } from "../utils/theme";
//   <Box sx={detailPage.shell}> … <Box sx={detailPage.headerStrip} /> <Paper sx={detailPage.headerBar}> …
//   <DataGrid sx={detailPage.grid} … />
//   <Card sx={detailPage.mobileCard}> <Box sx={detailPage.mobileCardStrip(avatarColor)} /> …
//
// All colours come from `colors` above, so changing the colour scheme restyles these too.
const _n = colors.neutral, _p = colors.primary;

export const detailPage = {
  // page shell: below the app bar, scrolls inside
  shell: { pt: 0.7, px: { xs: 2, sm: 3, md: 4 }, display: 'flex', flexDirection: 'column', height: `calc(100vh - ${APPBAR_HEIGHT}px)` },

  // header = thin gradient strip + white title bar (title left, actions right)
  headerWrap: { mb: 1.5 },
  headerStrip: { height: 4, background: gradients.primaryRow, borderRadius: '6px 6px 0 0' },
  headerBar: {
    border: '1px solid', borderColor: 'divider', borderTop: 0, borderRadius: '0 0 8px 8px',
    px: 2, py: 1.2, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
  },
  headerTitle: { fontSize: '1.05rem', fontWeight: fonts.weights.bold, color: _p.main },
  headerActions: { display: 'flex', gap: 1 },

  // content card that holds the grid / cards
  content: { borderRadius: 2, overflow: 'hidden', flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, marginBottom: '12px' },
  contentInner: { p: 0.5, display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 },

  // MUI X DataGrid: tinted header, uppercase titles, zebra rows, brand-tinted hover / selection
  grid: {
    '& .MuiDataGrid-columnHeaders': { backgroundColor: _n.gridHeader, borderBottom: `1px solid ${_n.border}` },
    '& .MuiDataGrid-columnHeaderTitle': { fontWeight: fonts.weights.bold },
    '& .MuiDataGrid-columnHeader': { backgroundColor: _n.gridHeader, color: _n.ink, fontWeight: fonts.weights.bold, fontSize: '0.74rem', textTransform: 'uppercase', letterSpacing: '0.06em' },
    '& .MuiDataGrid-filler': { backgroundColor: _n.surface },
    '& .MuiDataGrid-scrollbarFiller': { backgroundColor: _n.gridHeader },
    '& .MuiDataGrid-row:nth-of-type(odd)': { backgroundColor: _n.surface },
    '& .MuiDataGrid-row:nth-of-type(even)': { backgroundColor: _n.surfaceAlt },
    '& .MuiDataGrid-row:hover': { backgroundColor: _p.tint },
    '& .MuiDataGrid-row.Mui-selected': { backgroundColor: `${_p.selected} !important` },
    '& .MuiDataGrid-cell': { display: 'flex', alignItems: 'center' },
  },

  // mobile card view
  mobileCard: { mb: 1.5, boxShadow: 1, borderRadius: 2, overflow: 'hidden', transition: '0.2s', '&:hover': { boxShadow: 3 }, cursor: 'pointer' },
  mobileCardStrip: (color) => ({ height: 5, background: `linear-gradient(90deg, ${color} 0%, ${color}88 100%)` }),
  mobileCardContent: { py: 1.5, px: 1.5, pb: '10px !important' },
  mobileCardTitle: { color: _p.main, fontWeight: fonts.weights.bold, fontSize: '0.95rem', lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  mobileChip: { fontSize: '0.7rem', height: 22 },
  mobileSearch: { mb: 2, px: 1 },
  mobilePagination: { display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2, py: 2, mt: 1 },

  // bulk-action bar (mobile, shown when rows are selected)
  bulkBar: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1.5, mb: 2, bgcolor: 'background.paper', borderRadius: 2, boxShadow: 1 },

  // delete-confirmation dialog (red strip, or a red progress bar while deleting)
  deleteDialog: {
    strip: { height: 4, background: gradients.danger },
    titleBox: { px: 2.5, pt: 2, pb: 1.5 },
    title: { color: 'error.main', fontSize: '1.05rem', fontWeight: fonts.weights.bold },
    bodyBox: { px: 2.5, py: 2 },
    footer: { px: 2.5, py: 1.5, display: 'flex', justifyContent: 'flex-end', gap: 1 },
  },

  // snackbar: bottom-centre, filled alert, 3s
  snackbar: { autoHideDuration: 3000, anchorOrigin: { vertical: 'bottom', horizontal: 'center' } },
};

// everything above, in one object (also exposed as `theme.custom`)
export const tokens = { colors, gradients, fonts, radii, shadows, primaryAlpha, APPBAR_HEIGHT, detailPage };

// ─── MUI theme (built from the tokens) ────────────────────────────────────────
const { primary, secondary, neutral } = colors;

const theme = createTheme({
  custom: tokens,

  // ─── Palette ────────────────────────────────────────────────────────────────
  palette: {
    primary:   { main: primary.main, dark: primary.dark, light: primary.light, contrastText: '#FFFFFF' },
    secondary: { main: secondary.main, dark: secondary.dark, light: secondary.light, contrastText: '#FFFFFF' },
    error:     { main: colors.error.main },
    warning:   { main: colors.warning.main },
    success:   { main: colors.success.main },
    info:      { main: colors.info.main },
    background: { default: neutral.bg, paper: neutral.surface },
    text: { primary: neutral.text, secondary: neutral.textSub, disabled: 'rgba(0,0,0,0.38)' },
    divider: neutral.divider,
    grey: colors.grey,
  },

  // ─── Shape ──────────────────────────────────────────────────────────────────
  shape: { borderRadius: radii.md },

  // ─── Typography ─────────────────────────────────────────────────────────────
  typography: {
    fontFamily: fonts.family,
    h4: { fontWeight: fonts.weights.bold, letterSpacing: '-0.02em' },
    h5: { fontWeight: fonts.weights.bold, letterSpacing: '-0.015em' },
    h6: { fontWeight: fonts.weights.bold, letterSpacing: '-0.01em' },
    subtitle1: { fontWeight: fonts.weights.semibold },
    subtitle2: { fontWeight: fonts.weights.semibold },
    body1: { fontWeight: fonts.weights.regular, lineHeight: 1.6 },
    body2: { fontWeight: fonts.weights.regular, lineHeight: 1.5 },
    caption: { fontSize: '0.75rem', lineHeight: 1.4 },
    button: { fontWeight: fonts.weights.semibold, textTransform: 'none', letterSpacing: '0.01em' },
    overline: { fontWeight: fonts.weights.bold, letterSpacing: '0.08em', fontSize: '0.7rem' },
  },

  // ─── Breakpoints (unchanged) ─────────────────────────────────────────────────
  breakpoints: {
    values: { xs: 0, sm: 600, md: 1000, lg: 1200, xl: 1536 },
  },

  // ─── Shadows (corporate: subtle, layered) ─────────────────────────────────
  shadows: [
    'none',
    '0 1px 2px rgba(15,23,42,0.05)',
    '0 1px 3px rgba(15,23,42,0.06), 0 2px 6px rgba(15,23,42,0.04)',
    '0 6px 12px rgba(15,23,42,0.049), 0 18px 32px rgba(15,23,42,0.065)',
    '0 8px 16px rgba(15,23,42,0.052), 0 22px 38px rgba(15,23,42,0.070)',
    '0 10px 20px rgba(15,23,42,0.055), 0 26px 44px rgba(15,23,42,0.075)',
    '0 12px 24px rgba(15,23,42,0.058), 0 30px 50px rgba(15,23,42,0.080)',
    '0 14px 28px rgba(15,23,42,0.061), 0 34px 56px rgba(15,23,42,0.085)',
    '0 16px 32px rgba(15,23,42,0.064), 0 38px 62px rgba(15,23,42,0.090)',
    '0 18px 36px rgba(15,23,42,0.067), 0 42px 68px rgba(15,23,42,0.095)',
    '0 20px 40px rgba(15,23,42,0.070), 0 46px 74px rgba(15,23,42,0.100)',
    '0 22px 44px rgba(15,23,42,0.073), 0 50px 80px rgba(15,23,42,0.105)',
    '0 24px 48px rgba(15,23,42,0.076), 0 54px 86px rgba(15,23,42,0.110)',
    '0 26px 52px rgba(15,23,42,0.079), 0 58px 92px rgba(15,23,42,0.115)',
    '0 28px 56px rgba(15,23,42,0.082), 0 62px 98px rgba(15,23,42,0.120)',
    '0 30px 60px rgba(15,23,42,0.085), 0 66px 104px rgba(15,23,42,0.125)',
    '0 32px 64px rgba(15,23,42,0.088), 0 70px 110px rgba(15,23,42,0.130)',
    '0 34px 68px rgba(15,23,42,0.091), 0 74px 116px rgba(15,23,42,0.135)',
    '0 36px 72px rgba(15,23,42,0.094), 0 78px 122px rgba(15,23,42,0.140)',
    '0 38px 76px rgba(15,23,42,0.097), 0 82px 128px rgba(15,23,42,0.145)',
    '0 40px 80px rgba(15,23,42,0.100), 0 86px 134px rgba(15,23,42,0.150)',
    '0 42px 84px rgba(15,23,42,0.103), 0 90px 140px rgba(15,23,42,0.155)',
    '0 44px 88px rgba(15,23,42,0.106), 0 94px 146px rgba(15,23,42,0.160)',
    '0 46px 92px rgba(15,23,42,0.109), 0 98px 152px rgba(15,23,42,0.165)',
    '0 48px 96px rgba(15,23,42,0.112), 0 102px 158px rgba(15,23,42,0.170)',
  ],

  // ─── Component overrides ─────────────────────────────────────────────────────
  components: {

    // Global baseline + scrollbar + CSS variables for non-MUI styling
    MuiCssBaseline: {
      styleOverrides: `
        :root {
          --rb-primary: ${primary.main};
          --rb-primary-dark: ${primary.dark};
          --rb-primary-light: ${primary.light};
          --rb-primary-tint: ${primary.tint};
          --rb-bg: ${neutral.bg};
          --rb-surface: ${neutral.surface};
          --rb-ink: ${neutral.ink};
          --rb-text: ${neutral.text};
          --rb-text-sub: ${neutral.textSub};
          --rb-border: ${neutral.border};
          --rb-success: ${colors.success.main};
          --rb-warning: ${colors.warning.main};
          --rb-error: ${colors.error.main};
          --rb-font: ${fonts.family};
        }
        *, *::before, *::after { box-sizing: border-box; }
        body { margin: 0; -webkit-font-smoothing: antialiased; background-color: ${neutral.bg}; }
        ::-webkit-scrollbar { width: 6px; height: 6px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: ${primaryAlpha(0.30)}; border-radius: 3px; }
        ::-webkit-scrollbar-thumb:hover { background: ${primaryAlpha(0.55)}; }
        /* premium polish: aligned digits in tables/grids, crisp text, soft text selection */
        .MuiDataGrid-cell, .MuiTableCell-root { font-variant-numeric: tabular-nums; }
        body { text-rendering: optimizeLegibility; }
        ::selection { background: ${primaryAlpha(0.18)}; }
        a { color: ${primary.main}; text-decoration: none; }
        a:hover { text-decoration: underline; }
      `,
    },

    // AppBar
    MuiAppBar: {
      styleOverrides: {
        root: {
          paddingLeft: '0 !important',
          paddingRight: '0 !important',
          backgroundImage: 'none',
        },
      },
    },

    // Toolbar
    MuiToolbar: {
      styleOverrides: {
        root: {
          paddingLeft: '0 !important',
          paddingRight: '0 !important',
          minHeight: APPBAR_HEIGHT,
        },
      },
    },

    // Button — gradient primary with a soft glow, gentle lift on hover
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: fonts.weights.semibold,
          borderRadius: radii.md,
          boxShadow: 'none',
          transition: 'transform 0.18s ease, box-shadow 0.18s ease, background-color 0.18s ease, border-color 0.18s ease',
          '&:hover': { boxShadow: 'none' },
        },
        containedPrimary: {
          background: gradients.primary,
          boxShadow: shadows.glowPrimary,
          '&:hover': { background: gradients.primary, boxShadow: shadows.glowPrimaryHover, transform: 'translateY(-1px)' },
          '&:active': { transform: 'translateY(0)', boxShadow: shadows.glowPrimary },
          '&.Mui-disabled': { background: neutral.surfaceMuted, boxShadow: 'none', color: neutral.muted },
        },
        contained: {
          boxShadow: '0 1px 3px rgba(15,23,42,0.18)',
          '&:hover': { transform: 'translateY(-1px)' },
          '&:active': { transform: 'translateY(0)', boxShadow: 'none' },
        },
        outlined: {
          borderColor: neutral.borderStrong,
          '&:hover': { borderColor: primary.main, background: primary.surface },
        },
        text: {
          '&:hover': { background: primaryAlpha(0.06) },
        },
        sizeLarge: { padding: '10px 24px', fontSize: '0.9375rem' },
        sizeMedium: { padding: '7px 18px' },
        sizeSmall: { padding: '4px 12px', fontSize: '0.8125rem' },
      },
    },

    // Card — soft layered elevation, hairline border
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: radii.xl,
          boxShadow: shadows.card,
          border: `1px solid ${neutral.border}`,
          backgroundImage: 'none',
          transition: 'box-shadow 0.22s ease',
          '&:hover': {
            boxShadow: '0 2px 4px rgba(15,23,42,0.05), 0 14px 32px rgba(15,23,42,0.09)',
          },
        },
      },
    },

    // Paper
    MuiPaper: {
      styleOverrides: {
        root: { backgroundImage: 'none' },
        elevation0: { boxShadow: 'none' },
        elevation1: { boxShadow: '0 1px 2px rgba(15,23,42,0.05)' },
        elevation2: { boxShadow: '0 1px 3px rgba(15,23,42,0.06), 0 2px 6px rgba(15,23,42,0.04)' },
        elevation3: { boxShadow: shadows.card },
        rounded: { borderRadius: radii.xl },
      },
    },

    // Table — corporate data density
    MuiTableHead: {
      styleOverrides: {
        root: {
          '& .MuiTableCell-head': {
            backgroundColor: primary.surface,
            fontWeight: fonts.weights.bold,
            fontSize: '0.74rem',
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
            color: neutral.textSub,
            borderBottom: `1px solid ${neutral.border}`,
            whiteSpace: 'nowrap',
          },
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&:hover': { backgroundColor: primaryAlpha(0.04) },
          '&.Mui-selected': {
            backgroundColor: primaryAlpha(0.08),
            '&:hover': { backgroundColor: primaryAlpha(0.12) },
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: `1px solid ${neutral.border}`,
          padding: '11px 16px',
          fontSize: '0.875rem',
        },
      },
    },

    // Chip
    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: fonts.weights.semibold,
          fontSize: '0.78rem',
          borderRadius: radii.sm,
        },
        colorPrimary: {
          backgroundColor: primary.light,
          color: primary.main,
        },
      },
    },

    // TextField
    MuiTextField: {
      defaultProps: { autoComplete: 'new-password' },
    },
    MuiOutlinedInput: {
      defaultProps: { autoComplete: 'new-password' },
      styleOverrides: {
        root: {
          borderRadius: radii.md,
          backgroundColor: neutral.field,
          transition: 'box-shadow 0.18s ease, background-color 0.18s ease',
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: primary.main,
          },
          '&.Mui-focused': {
            backgroundColor: neutral.surface,
            boxShadow: `0 0 0 4px ${primaryAlpha(0.12)}`,
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: primary.main,
              borderWidth: 2,
            },
          },
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: neutral.borderStrong,
          },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          '&.Mui-focused': { color: primary.main },
        },
      },
    },
    MuiInputBase: {
      defaultProps: { autoComplete: 'new-password' },
    },

    // Sidebar / navigation items
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: radii.sm,
          margin: '1px 0',
          transition: 'background 0.15s ease, color 0.15s ease',
          '&.Mui-selected': {
            backgroundColor: primaryAlpha(0.10),
            color: primary.main,
            '&:hover': { backgroundColor: primaryAlpha(0.14) },
            '& .MuiListItemIcon-root': { color: primary.main },
          },
          '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)' },
        },
      },
    },

    // Drawer
    MuiDrawer: {
      styleOverrides: {
        paper: {
          border: 'none',
          boxShadow: '2px 0 12px rgba(0,0,0,0.08)',
        },
      },
    },

    // Menus / popovers — floating cards
    MuiMenu: {
      styleOverrides: { paper: { borderRadius: radii.lg, border: `1px solid ${neutral.border}`, boxShadow: shadows.popover } },
    },
    MuiPopover: {
      styleOverrides: { paper: { borderRadius: radii.lg, boxShadow: shadows.popover } },
    },
    MuiBackdrop: {
      styleOverrides: { root: { backgroundColor: 'rgba(15,23,42,0.45)', '&.MuiBackdrop-invisible': { backgroundColor: 'transparent' } } },
    },

    // Divider
    MuiDivider: {
      styleOverrides: {
        root: { borderColor: 'rgba(0,0,0,0.08)' },
      },
    },

    // Switch
    MuiSwitch: {
      styleOverrides: {
        switchBase: {
          '&.Mui-checked': {
            color: primary.main,
            '& + .MuiSwitch-track': { backgroundColor: primary.main, opacity: 0.7 },
          },
        },
      },
    },

    // Checkbox
    MuiCheckbox: {
      styleOverrides: {
        root: {
          '&.Mui-checked': { color: primary.main },
        },
      },
    },

    // Radio
    MuiRadio: {
      styleOverrides: {
        root: {
          '&.Mui-checked': { color: primary.main },
        },
      },
    },

    // LinearProgress / circular
    MuiLinearProgress: {
      styleOverrides: {
        root: { borderRadius: 4, backgroundColor: colors.grey[100] },
        bar:  { borderRadius: 4 },
      },
    },

    // Tooltip — clean dark pill
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: neutral.text,
          fontSize: '0.78rem',
          fontWeight: fonts.weights.medium,
          borderRadius: radii.sm,
          padding: '6px 12px',
          boxShadow: '0 8px 20px rgba(15,23,42,0.25)',
        },
        arrow: { color: neutral.text },
      },
    },

    // Tab
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: fonts.weights.semibold,
          fontSize: '0.875rem',
          '&.Mui-selected': { color: primary.main },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: { backgroundColor: primary.main, height: 3, borderRadius: '3px 3px 0 0' },
      },
    },

    // Snackbar / Alert
    MuiAlert: {
      styleOverrides: {
        root: { borderRadius: radii.md, fontWeight: fonts.weights.medium },
      },
    },

    // Dialog
    MuiDialog: {
      styleOverrides: {
        paper: { borderRadius: 18, boxShadow: shadows.dialog, border: `1px solid ${neutral.border}` },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: { fontWeight: fonts.weights.bold, fontSize: '1.125rem', paddingBottom: 8 },
      },
    },

    // Skeleton
    MuiSkeleton: {
      styleOverrides: {
        root: { borderRadius: radii.xs },
      },
    },

    // Avatar
    MuiAvatar: {
      styleOverrides: {
        colorDefault: {
          backgroundColor: primary.light,
          color: primary.main,
          fontWeight: fonts.weights.bold,
        },
      },
    },

    // Select
    MuiSelect: {
      styleOverrides: {
        icon: { color: neutral.textSub },
      },
    },

    // MenuItem
    MuiMenuItem: {
      styleOverrides: {
        root: {
          borderRadius: radii.xs,
          margin: '1px 4px',
          fontSize: '0.875rem',
          '&.Mui-selected': {
            backgroundColor: primaryAlpha(0.10),
            color: primary.main,
            '&:hover': { backgroundColor: primaryAlpha(0.14) },
          },
          '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)' },
        },
      },
    },

    // Accordion
    MuiAccordion: {
      styleOverrides: {
        root: {
          borderRadius: `${radii.md}px !important`,
          border: '1px solid rgba(0,0,0,0.08)',
          boxShadow: 'none',
          '&:before': { display: 'none' },
          '&.Mui-expanded': {
            boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
          },
        },
      },
    },
    MuiAccordionSummary: {
      styleOverrides: {
        root: { borderRadius: radii.md, fontWeight: fonts.weights.semibold },
      },
    },

    // Pagination
    MuiPaginationItem: {
      styleOverrides: {
        root: {
          borderRadius: radii.sm,
          '&.Mui-selected': {
            backgroundColor: primary.main,
            color: '#fff',
            '&:hover': { backgroundColor: primary.dark },
          },
        },
      },
    },
  },
});

export default theme;
